// TODO: delete this file when finished DP-6894
import React, { FC, useState, useContext, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import get from "lodash/get";
import groupBy from "lodash/groupBy";
import { useForm, FormProvider } from "react-hook-form";

import { IcAdd } from "components/SvgIcons";
import { SvgIcon } from "components/Icon";
import Button from "components/Button";
import CircularProgress from "components/CircularProgress";
import Grid from "components/Grid";
import Modal from "components/Modal";
import Typography from "components/Typography";

import ConfirmModal from "components/Modal/ConfirmModal";
import nanoid from "utils/nanoid";
import { GuidelineContext } from "utils/context";
import { BANK_ACCOUNT_LIMIT, ENTERPRISE_BANK_ACCOUNT_LIMIT } from "config/payment";

import {
  BANK_ID_PREFIX,
  COD_ID_KEY,
  COD_PREFIX,
  COD_THRESHOLD_KEY,
  NEW_BANK_ID_PREFIX,
  PAYMENT_GATEWAY_ID_KEY,
  PAYMENT_GATEWAY_PREFIX,
  PAYMENT_TYPES,
  PROMPT_PAY_CUSTOM_QR_IMAGE,
  PROMPT_PAY_ID_KEY,
  PROMPT_PAY_PREFIX,
  PAYMENT_GATEWAY_TYPE_KEY,
  PAYMENT_GATEWAY_API_KEY,
  PAYMENT_GATEWAY_MERCHANT_ID,
  PAYMENT_GATEWAY_MD5_SECRET_KEY,
  SECOND_PAYMENT_GATEWAY_PREFIX,
  SECOND_PAYMENT_GATEWAY_ID_KEY,
  PAYMENT_GATEWAY_DESCRIPTION_KEY,
  PAYMENT_GATEWAY_CUSTOM_HEADER,
  PAYMENT_GATEWAY_CUSTOM_URL,
  PAYMENT_GATEWAY_CUSTOM_BODY,
  PAYMENT_GATEWAY_CUSTOM_ICON_URL,
} from "constants/Payment";

import { PAYMENTS } from "graphql/payment/query";
import { ProjectIdType } from "types/Project";
import {
  BankAccountPaymentType,
  NewBankAccountPaymentType,
  PaymentGatewayChillPayType,
  PaymentGateway2C2PType,
  PaymentFormDataType,
  PaymentType,
  PaymentGatewayCustom,
} from "types/Payment";
import useGetProject from "utils/hooks/useGetProject";

import useIsDesktop from "utils/hooks/useIsDesktop";
import { DEEPLE_PAY_IMAGE_URL } from "constants/Chat";
import { DEEPLE_PAY, PAYMENT_CHANNEL, PROJECT } from "constants/Router";
import PaymentCard from "domain/Payment/PaymentCard";
import BankAccountForm from "domain/Payment/InputForm/BankAccountForm";
import PromptPayForm from "domain/Payment/InputForm/PromptPayForm";
import CODForm from "domain/Payment/InputForm/CODForm";
import PaymentGatewayForm from "domain/Payment/InputForm/PaymentGatewayForm";
import getPaymentSchema from "domain/Payment/validateSchema";
import useDeleteBankPayment from "domain/Payment/hooks/useDeleteBankPayment";
import useSavePayment from "domain/Payment/hooks/useSavePayment";
import { DeeplePayWrapper, PaymentCardWrapper, StatusBox } from "domain/Payment/styled";
import { Item, StickyButton } from "../styled";

const OldPayment: FC = () => {
  const { t } = useTranslation();
  const { projectId } = useParams<ProjectIdType>();
  const { setGuidelineCompletedStepCount } = useContext(GuidelineContext);
  const { isCurrentProjectEnterprise, isPaymentGatewayAddonEnabled, isDeeplePayEnabled } = useGetProject(projectId);
  const isDesktop = useIsDesktop();

  // const [isEnableSaveButton, setIsEnableSaveButton] = useState(false);
  const [isFirstTimeGetQueryData, setIsFirstTimeGetQueryData] = useState(false);
  const [editedBankPaymentIds, setEditedBankPaymentIds] = useState<string[]>([]);
  const [newBankPayments, setNewBankPayments] = useState<NewBankAccountPaymentType[]>([]);
  const [focusedBankPayment, setFocusedBankPayment] = useState<string>();
  const [editingPaymentGatewayIndex, setEditingPaymentGatewayIndex] = useState<0 | 1 | 2>(0);
  const [newPaymentGateway, setNewPaymentGateway] = useState<PaymentFormDataType | undefined>();
  const [newSecondPaymentGateway, setNewSecondPaymentGateway] = useState<PaymentFormDataType | undefined>();

  const methods = useForm({
    resolver: yupResolver(getPaymentSchema(newBankPayments, editedBankPaymentIds, true)),
  });

  const { register, handleSubmit, setValue, clearErrors } = methods;

  const { loading, data } = useQuery(PAYMENTS, {
    variables: {
      projectId,
    },
  });

  const { handleConfirmDelete, isOpenConfirmDeleteModal, openConfirmModal, closeConfirmModal } = useDeleteBankPayment(
    projectId,
  );

  const showSaveButton = useCallback(() => {
    // if (!isEnableSaveButton) {
    //   setIsEnableSaveButton(true);
    // }
  }, []);

  const handleCancelEditPaymentGateway = () => {
    setEditingPaymentGatewayIndex(0);
  };

  const handleSaveNewPaymentGatewayData = (paymentGatewayFormData: PaymentFormDataType) => {
    setNewPaymentGateway(paymentGatewayFormData);
    showSaveButton();
  };

  const handleSaveNewSecondPaymentGatewayData = (paymentGatewayFormData: PaymentFormDataType) => {
    setNewSecondPaymentGateway(paymentGatewayFormData);
    showSaveButton();
  };

  const handleChangeBankPayment = (id: string, fieldName: string, value: string) => {
    const validateKey = `${id}${fieldName}`;

    const regex = new RegExp(`^${BANK_ID_PREFIX}.*`, "i");
    const isExistingBankPayment = regex.test(id);

    if (isExistingBankPayment) {
      const newEditedBankPaymentIds = Array.from(new Set([...editedBankPaymentIds, id]));

      setEditedBankPaymentIds(newEditedBankPaymentIds);
    }

    if (fieldName && value) {
      register({ name: validateKey });
      setValue(validateKey, value);

      if (["BankName", "BankAccountType"].includes(fieldName)) {
        clearErrors(validateKey);
      }
    }
  };

  const handleChangeCOD = (fieldName: string, value: string) => {
    const validateKey = `${COD_PREFIX}${fieldName}`;

    register({ name: validateKey });
    setValue(validateKey, value);
  };

  const handleChangeIsActive = (paymentId: string, isActive: boolean) => {
    register({ name: `${paymentId}IsActive` });
    setValue(`${paymentId}IsActive`, isActive);

    const regex = new RegExp(`^${BANK_ID_PREFIX}.*`, "i");
    const isExistingBankPayment = regex.test(paymentId);

    if (isExistingBankPayment) {
      const newEditedBankPaymentIds = Array.from(new Set([...editedBankPaymentIds, paymentId]));

      setEditedBankPaymentIds(newEditedBankPaymentIds);
    }

    showSaveButton();
  };

  const handleRemoveBankPayment = (bankPaymentId: string) => {
    setFocusedBankPayment(bankPaymentId);
    openConfirmModal();
  };

  const handleRemoveNewBankPayment = (bankPaymentId: string) => {
    const filteredBankPayments = newBankPayments.filter(({ id }: NewBankAccountPaymentType) => id !== bankPaymentId);
    setNewBankPayments(filteredBankPayments);
  };

  useEffect(() => {
    register({ name: PROMPT_PAY_CUSTOM_QR_IMAGE });
  }, [register]);

  useEffect(() => {
    if (editingPaymentGatewayIndex === 0 && newSecondPaymentGateway && newSecondPaymentGateway.merchantId === "") {
      setNewSecondPaymentGateway(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingPaymentGatewayIndex]);

  useEffect(() => {
    if (data) {
      const [paymentGateway1, paymentGateway2] = data.payments.filter(
        ({ type }: PaymentType) =>
          type !== PAYMENT_TYPES.BANK_ACCOUNT &&
          type !== PAYMENT_TYPES.PROMPT_PAY &&
          type !== PAYMENT_TYPES.CASH_ON_DELIVERY,
      );

      const isPaymentGatewayActive = get(paymentGateway1, "isActive", false);
      handleChangeIsActive(PAYMENT_GATEWAY_PREFIX, isPaymentGatewayActive);

      const paymentGatewayId = get(paymentGateway, "id");
      if (paymentGatewayId) {
        register({ name: PAYMENT_GATEWAY_ID_KEY });
        setValue(PAYMENT_GATEWAY_ID_KEY, paymentGatewayId);
      }

      // Second payment gateway
      const isSecondPaymentGatewayActive = get(paymentGateway2, "isActive", false);
      handleChangeIsActive(SECOND_PAYMENT_GATEWAY_PREFIX, isSecondPaymentGatewayActive);

      const secondPaymentGatewayId = get(secondPaymentGateway, "id");
      if (secondPaymentGatewayId) {
        register({ name: SECOND_PAYMENT_GATEWAY_ID_KEY });
        setValue(SECOND_PAYMENT_GATEWAY_ID_KEY, secondPaymentGatewayId);
      }
    }
    // setIsEnableSaveButton(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const {
    handleSavePayment: onSavePayment,
    isOpenWarningActivePaymentModal,
    closeWarningActivePaymentModal,
  } = useSavePayment({
    projectId,
    onComplete: () => {
      setIsFirstTimeGetQueryData(false);
      // setIsEnableSaveButton(false);
      setGuidelineCompletedStepCount(projectId);
      setNewPaymentGateway(undefined);
      setNewSecondPaymentGateway(undefined);
      setEditedBankPaymentIds([]);
      setNewBankPayments([]);
    },
    onChangeIsActive: handleChangeIsActive,
    onChangeBankPayment: handleChangeBankPayment,
  });

  if (loading) {
    return (
      <Modal isOpen onClose={() => {}}>
        <CircularProgress className="m-4" />
      </Modal>
    );
  }

  let bankAccountPayments = [];
  let promptPayPayment = null;
  let codPayment = null;
  let paymentGateway: PaymentGatewayChillPayType | PaymentGateway2C2PType | PaymentGatewayCustom | null | false = null;
  let secondPaymentGateway:
    | PaymentGatewayChillPayType
    | PaymentGateway2C2PType
    | PaymentGatewayCustom
    | null
    | false = null;
  if (data && data.payments) {
    const groupPayments = groupBy(data.payments, "type");

    bankAccountPayments = groupPayments[PAYMENT_TYPES.BANK_ACCOUNT] || [];
    promptPayPayment = groupPayments[PAYMENT_TYPES.PROMPT_PAY] && groupPayments[PAYMENT_TYPES.PROMPT_PAY][0];
    codPayment = groupPayments[PAYMENT_TYPES.CASH_ON_DELIVERY] && groupPayments[PAYMENT_TYPES.CASH_ON_DELIVERY][0];
    const [paymentGateway1, paymentGateway2] = data.payments.filter(
      ({ type }: PaymentType) =>
        type !== PAYMENT_TYPES.BANK_ACCOUNT &&
        type !== PAYMENT_TYPES.PROMPT_PAY &&
        type !== PAYMENT_TYPES.CASH_ON_DELIVERY,
    );
    paymentGateway = paymentGateway1;
    secondPaymentGateway = paymentGateway2;
  }

  const bankAccountPaymentAmount = bankAccountPayments.length + newBankPayments.length;
  const isShowDeleteIcon = bankAccountPaymentAmount > 1;
  const bankAccountPaymentLimit = isCurrentProjectEnterprise ? ENTERPRISE_BANK_ACCOUNT_LIMIT : BANK_ACCOUNT_LIMIT;
  const isShowAddNewBankButton = bankAccountPaymentAmount < bankAccountPaymentLimit;

  const handleAddNewBankPayment = () => {
    const isNewBankActive = !!bankAccountPaymentAmount;

    const initBankPayment = {
      id: NEW_BANK_ID_PREFIX + nanoid(),
      isActive: isNewBankActive,
      type: PAYMENT_TYPES.BANK_ACCOUNT,
    };
    const combinedBankPayments = [...newBankPayments, initBankPayment];

    handleChangeIsActive(initBankPayment.id, isNewBankActive);
    setNewBankPayments(combinedBankPayments);
  };

  const handleAddNewPaymentGateway = () => {
    setNewSecondPaymentGateway({
      apiKey: "",
      merchantId: "",
      md5Secret: "",
      type: PAYMENT_TYPES.PAYMENT_GATEWAY_2C2P,
    });
    setEditingPaymentGatewayIndex(2);
  };

  const handleSavePayment = (formData: PaymentFormDataType) => {
    onSavePayment(
      formData,
      paymentGateway,
      secondPaymentGateway,
      newBankPayments,
      editedBankPaymentIds,
      newPaymentGateway,
      newSecondPaymentGateway,
    );

    // eslint-disable-next-line
    if ((formData[COD_THRESHOLD_KEY] as string) == "-0") {
      setValue(COD_THRESHOLD_KEY, 0);
    }
  };

  const defaultApiKey = newPaymentGateway ? newPaymentGateway[PAYMENT_GATEWAY_API_KEY] : get(paymentGateway, "apiKey");
  const defaultMerchantId = newPaymentGateway
    ? newPaymentGateway[PAYMENT_GATEWAY_MERCHANT_ID]
    : get(paymentGateway, "merchantId");
  const defaultMd5Secret = newPaymentGateway
    ? newPaymentGateway[PAYMENT_GATEWAY_MD5_SECRET_KEY]
    : get(paymentGateway, "md5Secret");
  const defaultPaymentGatewayType = newPaymentGateway
    ? newPaymentGateway[PAYMENT_GATEWAY_TYPE_KEY]
    : get(paymentGateway, "type");
  const defaultPaymentGatewayDescription = newPaymentGateway
    ? newPaymentGateway[PAYMENT_GATEWAY_DESCRIPTION_KEY]
    : get(paymentGateway, "description");

  const defaultCustomPaymentUrl = newPaymentGateway
    ? newPaymentGateway[PAYMENT_GATEWAY_CUSTOM_URL]
    : get(paymentGateway, "url");
  const defaultCustomPaymentHeader = newPaymentGateway
    ? newPaymentGateway[PAYMENT_GATEWAY_CUSTOM_HEADER]
    : get(paymentGateway, "header");
  const defaultCustomPaymentBody = newPaymentGateway
    ? newPaymentGateway[PAYMENT_GATEWAY_CUSTOM_BODY]
    : get(paymentGateway, "body");
  const defaultCustomPaymentIconUrl = newPaymentGateway
    ? newPaymentGateway[PAYMENT_GATEWAY_CUSTOM_ICON_URL]
    : get(paymentGateway, "iconUrl");

  const defaultSecondApiKey = newSecondPaymentGateway
    ? newSecondPaymentGateway[PAYMENT_GATEWAY_API_KEY]
    : get(secondPaymentGateway, "apiKey");
  const defaultSecondMerchantId = newSecondPaymentGateway
    ? newSecondPaymentGateway[PAYMENT_GATEWAY_MERCHANT_ID]
    : get(secondPaymentGateway, "merchantId");
  const defaultSecondMd5Secret = newSecondPaymentGateway
    ? newSecondPaymentGateway[PAYMENT_GATEWAY_MD5_SECRET_KEY]
    : get(secondPaymentGateway, "md5Secret");
  const defaultSecondPaymentGatewayType = newSecondPaymentGateway
    ? newSecondPaymentGateway[PAYMENT_GATEWAY_TYPE_KEY]
    : get(secondPaymentGateway, "type");
  const defaultSecondPaymentGatewayDescription = newSecondPaymentGateway
    ? newSecondPaymentGateway[PAYMENT_GATEWAY_DESCRIPTION_KEY]
    : get(secondPaymentGateway, "description");

  const defaultSecondCustomPaymentUrl = newSecondPaymentGateway
    ? newSecondPaymentGateway[PAYMENT_GATEWAY_CUSTOM_URL]
    : get(secondPaymentGateway, "url");
  const defaultSecondCustomPaymentHeader = newSecondPaymentGateway
    ? newSecondPaymentGateway[PAYMENT_GATEWAY_CUSTOM_HEADER]
    : get(secondPaymentGateway, "header");
  const defaultSecondCustomPaymentBody = newSecondPaymentGateway
    ? newSecondPaymentGateway[PAYMENT_GATEWAY_CUSTOM_BODY]
    : get(secondPaymentGateway, "body");
  const defaultSecondCustomPaymentIconUrl = newSecondPaymentGateway
    ? newSecondPaymentGateway[PAYMENT_GATEWAY_CUSTOM_ICON_URL]
    : get(secondPaymentGateway, "iconUrl");

  if (!isFirstTimeGetQueryData && data) {
    if (bankAccountPayments && !bankAccountPayments.length) {
      handleAddNewBankPayment();
    } else if (bankAccountPayments) {
      bankAccountPayments.forEach(({ id, isActive, bankName, bankAccountType }: BankAccountPaymentType) => {
        const bankIdWithPrefix = `${BANK_ID_PREFIX}${id}`;

        handleChangeIsActive(bankIdWithPrefix, isActive);
        handleChangeBankPayment(bankIdWithPrefix, "BankName", bankName);
        handleChangeBankPayment(bankIdWithPrefix, "BankAccountType", bankAccountType);
      });
    }

    // Promptpay
    const isPromptPayActive = get(promptPayPayment, "isActive", false);
    handleChangeIsActive(PROMPT_PAY_PREFIX, isPromptPayActive);

    const promptpayId = get(promptPayPayment, "id");
    if (promptpayId) {
      register({ name: PROMPT_PAY_ID_KEY });
      setValue(PROMPT_PAY_ID_KEY, promptpayId);
    }

    // COD
    const isCODActive = get(codPayment, "isActive", false);
    handleChangeIsActive(COD_PREFIX, isCODActive);

    if (codPayment) {
      const { id, shippingMethod, depositType, managementFee } = codPayment;
      const managementFeeType = managementFee?.type;

      register({ name: COD_ID_KEY });
      setValue(COD_ID_KEY, id);

      handleChangeCOD("ShippingMethod", shippingMethod);
      handleChangeCOD("DepositType", depositType);
      handleChangeCOD("ManagementFeeType", managementFeeType);
    }

    // Payment gateway
    const isPaymentGatewayActive = get(paymentGateway, "isActive", false);
    handleChangeIsActive(PAYMENT_GATEWAY_PREFIX, isPaymentGatewayActive);

    const paymentGatewayId = get(paymentGateway, "id");
    if (paymentGatewayId) {
      register({ name: PAYMENT_GATEWAY_ID_KEY });
      setValue(PAYMENT_GATEWAY_ID_KEY, paymentGatewayId);
    }

    // Second payment gateway
    const isSecondPaymentGatewayActive = get(secondPaymentGateway, "isActive", false);
    handleChangeIsActive(SECOND_PAYMENT_GATEWAY_PREFIX, isSecondPaymentGatewayActive);

    const secondPaymentGatewayId = get(secondPaymentGateway, "id");
    if (secondPaymentGatewayId) {
      register({ name: SECOND_PAYMENT_GATEWAY_ID_KEY });
      setValue(SECOND_PAYMENT_GATEWAY_ID_KEY, secondPaymentGatewayId);
    }

    setIsFirstTimeGetQueryData(true);
    // setIsEnableSaveButton(false);
    setEditedBankPaymentIds([]);
  }

  return (
    <>
      {data && (
        <FormProvider {...methods}>
          <Item container alignItems="center" className={isDesktop ? "px-4 py-3 mt-3" : "mobile px-4 py-3 mt-3"}>
            {/* DEEPLE PAY */}
            {isDeeplePayEnabled && (
              <div className="d-none">
                <PaymentCardWrapper item xs={12}>
                  <DeeplePayWrapper className="p-3">
                    <StatusBox>
                      <span />
                      <Typography variant="body4">
                        {isDeeplePayEnabled
                          ? t("paymentMethod.deeplePay.status.active")
                          : t("paymentMethod.deeplePay.status.inActive")}
                      </Typography>
                    </StatusBox>
                    <img
                      className="pb-2 mb-1"
                      src={`${DEEPLE_PAY_IMAGE_URL}/deeple-pay-logo.png`}
                      alt="deeple-pay-logo"
                    />
                    <Typography variant="title8">
                      {t("paymentMethod.deeplePay.description")} &nbsp;
                      <a href={`/${PROJECT}/${projectId}/${PAYMENTS}/${PAYMENT_CHANNEL}`}>
                        {t("paymentMethod.deeplePay.link")}
                      </a>
                    </Typography>
                  </DeeplePayWrapper>
                </PaymentCardWrapper>
              </div>
            )}

            {/* BANK_ACCOUNT */}
            <PaymentCardWrapper item xs={12}>
              <Grid item container xs={12} justify="space-between" alignItems="center" className="pb-3">
                <Grid item>
                  <Typography variant="title2" color="dark">
                    {t("Bank account")}
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography variant="body3">
                    {bankAccountPaymentAmount} / {bankAccountPaymentLimit}
                  </Typography>
                </Grid>
              </Grid>

              {bankAccountPayments.map((bankAccountPayment: BankAccountPaymentType, index: number) => {
                const {
                  isActive,
                  id,
                  accountName,
                  accountNumber,
                  bankName,
                  bankBranch,
                  bankAccountType,
                } = bankAccountPayment;

                const bankIdWithPrefix = `${BANK_ID_PREFIX}${id}`;

                return (
                  <div key={bankIdWithPrefix} className={`bank-account-${index}`}>
                    <PaymentCard
                      isActive={isActive}
                      onChangeIsActive={(value: boolean) => handleChangeIsActive(bankIdWithPrefix, value)}
                      isShowDeleteIcon={isShowDeleteIcon}
                      onDelete={() => handleRemoveBankPayment(id)}
                    >
                      <BankAccountForm
                        id={bankIdWithPrefix}
                        accountName={accountName}
                        accountNumber={accountNumber}
                        bankName={bankName}
                        bankBranch={bankBranch}
                        bankAccountType={bankAccountType}
                        onChange={(fieldName: string, value: string) =>
                          handleChangeBankPayment(bankIdWithPrefix, fieldName, value)
                        }
                        showSaveButton={showSaveButton}
                      />
                    </PaymentCard>
                  </div>
                );
              })}

              {newBankPayments.map((newBank: NewBankAccountPaymentType, index: number) => {
                const { id, isActive } = newBank;

                return (
                  <div key={id} className={`new-bank-account-${index}`}>
                    <PaymentCard
                      isActive={isActive}
                      onChangeIsActive={(value: boolean) => handleChangeIsActive(id, value)}
                      isShowDeleteIcon={isShowDeleteIcon}
                      onDelete={() => handleRemoveNewBankPayment(id)}
                    >
                      <BankAccountForm
                        id={id}
                        onChange={(fieldName: string, value: string) => handleChangeBankPayment(id, fieldName, value)}
                        showSaveButton={showSaveButton}
                      />
                    </PaymentCard>
                  </div>
                );
              })}
              {isShowAddNewBankButton && (
                <Button fullWidth color="secondary" className="mt-3" onClick={handleAddNewBankPayment}>
                  <SvgIcon component={IcAdd} fontSize="small" />
                  <Typography variant="body3">{t("Add new bank account")}</Typography>
                </Button>
              )}
            </PaymentCardWrapper>

            {/* PROMPT_PAY */}
            <PaymentCardWrapper item xs={12}>
              <Typography variant="title2" color="dark" className="pb-3">
                {t("PromptPay account")}
              </Typography>
              <PaymentCard
                isActive={get(promptPayPayment, "isActive", false)}
                onChangeIsActive={(value: boolean) => handleChangeIsActive(PROMPT_PAY_PREFIX, value)}
              >
                <PromptPayForm
                  accountName={get(promptPayPayment, "accountName")}
                  accountNumber={get(promptPayPayment, "accountNumber")}
                  image={get(promptPayPayment, "image")}
                  showSaveButton={showSaveButton}
                  setValue={setValue}
                />
              </PaymentCard>
            </PaymentCardWrapper>

            {/* CASH_ON_DELIVERY */}
            <div className="d-none">
              <PaymentCardWrapper item xs={12} className={!isPaymentGatewayAddonEnabled ? "mb-5" : ""}>
                <Typography variant="title2" color="dark" className="pb-3">
                  {t("Cash on Delivery (COD)")}
                </Typography>
                <PaymentCard
                  isActive={get(codPayment, "isActive", false)}
                  onChangeIsActive={(value: boolean) => handleChangeIsActive(COD_PREFIX, value)}
                >
                  <CODForm
                    shippingMethod={get(codPayment, "shippingMethod")}
                    depositThreshold={get(codPayment, "depositThreshold")}
                    depositType={get(codPayment, "depositType")}
                    depositAmount={get(codPayment, "depositAmount")}
                    managementFee={get(codPayment, "managementFee")}
                    onChange={(fieldName: string, value: string) => handleChangeCOD(fieldName, value)}
                    showSaveButton={showSaveButton}
                  />
                </PaymentCard>
              </PaymentCardWrapper>
            </div>

            {/* PAYMENT_GATEWAY */}
            {isPaymentGatewayAddonEnabled && (
              <PaymentCardWrapper item xs={12}>
                <Typography variant="title2" color="dark" className="pb-3">
                  {t("Payment gateway")}
                </Typography>
                <PaymentCard
                  isActive={get(paymentGateway, "isActive", false)}
                  onChangeIsActive={(value: boolean) => handleChangeIsActive(PAYMENT_GATEWAY_PREFIX, value)}
                  isShowEditIcon={!editingPaymentGatewayIndex}
                  onEdit={() => setEditingPaymentGatewayIndex(1)}
                >
                  <PaymentGatewayForm
                    apiKey={defaultApiKey}
                    merchantId={defaultMerchantId}
                    md5Secret={defaultMd5Secret}
                    defaultType={defaultPaymentGatewayType}
                    description={defaultPaymentGatewayDescription}
                    isEditingPaymentGateway={editingPaymentGatewayIndex === 1}
                    onCancelEditPaymentGateway={handleCancelEditPaymentGateway}
                    onSaveNewPaymentGatewayData={handleSaveNewPaymentGatewayData}
                    isNewPaymentGateway={Boolean(newPaymentGateway)}
                    customUrl={defaultCustomPaymentUrl}
                    customIconUrl={defaultCustomPaymentIconUrl}
                    customHeader={defaultCustomPaymentHeader}
                    customBody={defaultCustomPaymentBody}
                  />
                </PaymentCard>

                {isCurrentProjectEnterprise &&
                  (secondPaymentGateway || newSecondPaymentGateway ? (
                    <PaymentCard
                      isActive={get(secondPaymentGateway, "isActive", false)}
                      onChangeIsActive={(value: boolean) => handleChangeIsActive(SECOND_PAYMENT_GATEWAY_PREFIX, value)}
                      isShowEditIcon={!editingPaymentGatewayIndex}
                      onEdit={() => setEditingPaymentGatewayIndex(2)}
                    >
                      <PaymentGatewayForm
                        apiKey={defaultSecondApiKey}
                        merchantId={defaultSecondMerchantId}
                        md5Secret={defaultSecondMd5Secret}
                        defaultType={defaultSecondPaymentGatewayType}
                        description={defaultSecondPaymentGatewayDescription}
                        isEditingPaymentGateway={editingPaymentGatewayIndex === 2}
                        onCancelEditPaymentGateway={handleCancelEditPaymentGateway}
                        onSaveNewPaymentGatewayData={handleSaveNewSecondPaymentGatewayData}
                        isNewPaymentGateway={Boolean(newSecondPaymentGateway)}
                        customUrl={defaultSecondCustomPaymentUrl}
                        customIconUrl={defaultSecondCustomPaymentIconUrl}
                        customHeader={defaultSecondCustomPaymentHeader}
                        customBody={defaultSecondCustomPaymentBody}
                      />
                    </PaymentCard>
                  ) : (
                    <Button fullWidth color="secondary" className="mt-3" onClick={handleAddNewPaymentGateway}>
                      <SvgIcon component={IcAdd} fontSize="small" />
                      <Typography variant="body3">{t("paymentGateway.button.add")}</Typography>
                    </Button>
                  ))}
              </PaymentCardWrapper>
            )}
          </Item>
        </FormProvider>
      )}

      <StickyButton>
        <Button fullWidth onClick={handleSubmit(handleSavePayment)}>
          {t("Save")}
        </Button>
      </StickyButton>

      <Modal isOpen={isOpenWarningActivePaymentModal} onClose={closeWarningActivePaymentModal}>
        <Grid container justify="center" className="p-3">
          <Grid item xs={8} className="text-center py-3">
            <Typography variant="title8" color="dark">
              {t("Please active at least 1 payment method except COD")}
            </Typography>
          </Grid>
          <Grid item xs={12} className="pt-1 text-center">
            <Button size="small" color="ghost" fullWidth onClick={closeWarningActivePaymentModal}>
              {t("OK")}
            </Button>
          </Grid>
        </Grid>
      </Modal>

      <ConfirmModal
        title={t("Are you sure you want to remove this payment?")}
        confirmButtonText={t("Confirm")}
        isOpen={isOpenConfirmDeleteModal}
        onClose={closeConfirmModal}
        onClickConfirmButton={() => handleConfirmDelete(focusedBankPayment as string)}
      />
    </>
  );
};

export default OldPayment;
